import React, { useEffect, useState, useMemo, useRef } from "react";
import { LogoLight } from "../components/Logo";

/*
 * PAGE DE COMMUNICATION AUTONOME — /ouvrir-une-succession
 *
 * Optimisations conversion :
 *   - Bannière cookies masquée (via CookieConsent qui skip cette URL)
 *   - Mobile-first : spacing réduit, texte court sur XS, CTA above-the-fold
 *   - Carte crème atténuée pour ne plus voler la vedette au CTA
 *   - CTA plus punchy (léger pulse à l'apparition)
 *   - Sticky bar en bas quand le CTA du hero sort du viewport
 *   - Icônes SVG line-icons (identiques sur tous devices)
 *   - Ligne réassurance sous le bouton principal
 */

const CONNEXION_URL = "/ouvrir-une-succession/diagnostic";

// ---- Icônes SVG line-icons (uniformes multi-plateformes) --------------------
const IconGenealogiste = ({ size = 18, color = "currentColor" }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <circle cx="11" cy="11" r="6" />
    <line x1="21" y1="21" x2="15.5" y2="15.5" />
  </svg>
);
const IconGeometre = ({ size = 18, color = "currentColor" }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M3 20 L12 4 L21 20 Z" />
    <line x1="7.5" y1="15" x2="16.5" y2="15" />
  </svg>
);
const IconNotaire = ({ size = 18, color = "currentColor" }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <line x1="12" y1="3" x2="12" y2="20" />
    <line x1="8" y1="20" x2="16" y2="20" />
    <line x1="4" y1="7" x2="20" y2="7" />
    <path d="M4 7 L2.5 13 L6.5 13 Z" />
    <path d="M20 7 L18.5 13 L22.5 13 Z" />
  </svg>
);
const IconAvocat = ({ size = 18, color = "currentColor" }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M5 20 L5 8 L12 4 L19 8 L19 20 Z" />
    <path d="M9 20 L9 13 L15 13 L15 20" />
  </svg>
);
const IconMediateur = ({ size = 18, color = "currentColor" }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <circle cx="8" cy="8" r="3" />
    <circle cx="16" cy="8" r="3" />
    <path d="M3 20 c0 -3 2.5 -5 5 -5 s5 2 5 5" />
    <path d="M11 20 c0 -3 2.5 -5 5 -5 s5 2 5 5" />
  </svg>
);
// eslint-disable-next-line no-unused-vars
const IconHuissier = ({ size = 18, color = "currentColor" }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M6 3 L18 3 L18 8 L21 11 L21 20 L3 20 L3 11 L6 8 Z" />
    <line x1="9" y1="12" x2="15" y2="12" />
  </svg>
);

const ICONS = {
  genealogiste: IconGenealogiste,
  geometre: IconGeometre,
  notaire: IconNotaire,
  avocat: IconAvocat,
  mediateur: IconMediateur,
  huissier: IconHuissier,
};

// ---- 4 exemples fictifs -----------------------------------------------------
const EXAMPLES = [
  {
    id: "marie",
    titre: "Succession de Marie D.",
    tags: [
      { label: "Bien immobilier", color: "brass" },
      { label: "Dimension internationale", color: "teal" },
    ],
    pros: [
      { key: "genealogiste", label: "Généalogiste successoral" },
      { key: "geometre", label: "Géomètre-expert" },
      { key: "notaire", label: "Notaire" },
    ],
  },
  {
    id: "robert",
    titre: "Succession de Robert K.",
    tags: [{ label: "Plusieurs générations d'héritiers", color: "brass" }],
    pros: [
      { key: "genealogiste", label: "Généalogiste successoral" },
      { key: "notaire", label: "Notaire" },
      { key: "geometre", label: "Géomètre-expert" },
    ],
  },
  {
    id: "sandra",
    titre: "Succession de Sandra L.",
    tags: [{ label: "Climat familial", color: "teal" }],
    pros: [
      { key: "mediateur", label: "Médiateur familial" },
      { key: "avocat", label: "Avocat" },
      { key: "notaire", label: "Notaire" },
    ],
  },
  {
    id: "paul",
    titre: "Succession de Paul R.",
    tags: [{ label: "Succession simple", color: "brass" }],
    pros: [{ key: "notaire", label: "Notaire" }],
  },
];

// ---- Charte -----------------------------------------------------------------
const C = {
  bgDark: "#0F2E29",
  bgMid: "#1F5E56",
  bgLight: "#2A6D63",
  brass: "#AD7C33",
  brassLight: "#E7C88B",
  brassDark: "#8A6427",
  cream: "#EAE3D2",       // ← plus sourd que #F6F2E9 : réduit le contraste avec le fond
  creamRule: "rgba(18,59,54,0.09)",
  ink: "#17242A",
  tealDark: "#123B36",
  white: "#FFFFFF",
  offWhite: "rgba(246,242,233,0.9)",
};

export default function OuvrirSuccession() {
  const [idx, setIdx] = useState(0);
  const [fade, setFade] = useState(true);
  const [heroCtaVisible, setHeroCtaVisible] = useState(true);
  const heroCtaRef = useRef(null);

  // Titre + polices Google Fonts scopées à cette page
  useEffect(() => {
    if (typeof document === "undefined") return;
    const prevTitle = document.title;
    document.title = "Ouvrir une succession · AUPRÉA";
    const id = "auprea-marketing-fonts";
    if (!document.getElementById(id)) {
      const p1 = document.createElement("link");
      p1.rel = "preconnect"; p1.href = "https://fonts.googleapis.com"; p1.id = id + "-1";
      document.head.appendChild(p1);
      const p2 = document.createElement("link");
      p2.rel = "preconnect"; p2.href = "https://fonts.gstatic.com"; p2.crossOrigin = "anonymous"; p2.id = id + "-2";
      document.head.appendChild(p2);
      const link = document.createElement("link");
      link.rel = "stylesheet"; link.id = id;
      link.href = "https://fonts.googleapis.com/css2?family=Fraunces:opsz,wght@9..144,400;9..144,500;9..144,600&family=Public+Sans:wght@400;500;600;700&display=swap";
      document.head.appendChild(link);
    }
    return () => { document.title = prevTitle; };
  }, []);

  // Détecte quand le CTA du hero sort du viewport pour afficher la sticky bar
  useEffect(() => {
    if (!heroCtaRef.current || typeof IntersectionObserver === "undefined") return;
    const observer = new IntersectionObserver(
      ([entry]) => setHeroCtaVisible(entry.intersectionRatio >= 0.9),
      { threshold: [0, 0.9, 1] }
    );
    observer.observe(heroCtaRef.current);
    return () => observer.disconnect();
  }, []);

  const current = useMemo(() => EXAMPLES[idx], [idx]);

  const nextExample = () => {
    setFade(false);
    setTimeout(() => {
      setIdx((i) => (i + 1) % EXAMPLES.length);
      setFade(true);
    }, 180);
  };

  const fontSerif = '"Fraunces", "Playfair Display", Georgia, serif';
  const fontSans = '"Public Sans", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';

  return (
    <div
      data-testid="marketing-succession-page"
      style={{
        minHeight: "100vh",
        background: `radial-gradient(1200px 700px at 90% -10%, rgba(231,200,139,0.14) 0%, transparent 55%), linear-gradient(180deg, ${C.bgDark} 0%, ${C.bgMid} 60%, ${C.bgLight} 100%)`,
        color: C.white,
        fontFamily: fontSans,
        display: "flex",
        flexDirection: "column",
        paddingBottom: 90, // marge pour la sticky bar mobile
      }}
    >
      {/* Barre laiton fine */}
      <div
        style={{ height: 4, background: `linear-gradient(90deg, ${C.brassDark} 0%, ${C.brassLight} 50%, ${C.brassDark} 100%)` }}
        data-testid="brass-bar"
      />

      {/* Header logo seul */}
      <header style={{ padding: "20px 24px 4px", textAlign: "center" }}>
        <div data-testid="wordmark" style={{ display: "inline-block" }}>
          <LogoLight size={30} />
        </div>
      </header>

      {/* CSS scoped à la page (media queries + animations) */}
      <style>{`
        @keyframes aupreaCtaPulse {
          0%   { box-shadow: 0 6px 22px rgba(173,124,51,0.35), 0 0 0 0 rgba(231,200,139,0.5); }
          70%  { box-shadow: 0 6px 22px rgba(173,124,51,0.35), 0 0 0 14px rgba(231,200,139,0); }
          100% { box-shadow: 0 6px 22px rgba(173,124,51,0.35), 0 0 0 0 rgba(231,200,139,0); }
        }
        .marketing-cta {
          animation: aupreaCtaPulse 2.4s ease-out 0.7s 2;
        }
        .marketing-cta:hover { transform: translateY(-1px); box-shadow: 0 10px 28px rgba(0,0,0,0.28); }
        .marketing-example-fade { transition: opacity 180ms ease; }

        .marketing-grid { grid-template-columns: 1fr; gap: 20px !important; }
        @media (min-width: 900px) {
          .marketing-grid { grid-template-columns: 1.15fr 1fr !important; gap: 64px !important; }
        }

        .marketing-hero-title { font-size: clamp(24px, 6vw, 52px); }
        .marketing-hero-body-full { display: none; }
        .marketing-hero-body-short { display: inline; }
        @media (min-width: 640px) {
          .marketing-hero-body-full { display: inline; }
          .marketing-hero-body-short { display: none; }
        }

        /* Mobile : compression verticale pour placer le CTA au-dessus de la ligne de flottaison */
        .marketing-eyebrow { margin-bottom: 10px; font-size: 11px; }
        .marketing-hero-body { margin-top: 12px; margin-bottom: 16px; font-size: 15px; line-height: 1.55; }
        .marketing-badges { margin-bottom: 16px; gap: 6px; }
        .marketing-badge { padding: 5px 11px; font-size: 11.5px; }
        .marketing-price-note { margin-top: 14px; font-size: 12px; }
        @media (min-width: 640px) {
          .marketing-eyebrow { margin-bottom: 18px; font-size: 12px; }
          .marketing-hero-body { margin-top: 22px; margin-bottom: 26px; font-size: 17px; line-height: 1.7; }
          .marketing-badges { margin-bottom: 30px; gap: 10px; }
          .marketing-badge { padding: 7px 14px; font-size: 12.5px; }
          .marketing-price-note { margin-top: 22px; font-size: 13px; }
        }

        /* Sticky bottom bar (visible seulement quand hero CTA hors viewport) */
        .marketing-sticky {
          position: fixed;
          left: 0; right: 0; bottom: 0;
          background: linear-gradient(180deg, rgba(15,46,41,0.94) 0%, rgba(15,46,41,0.98) 100%);
          backdrop-filter: blur(8px);
          border-top: 2px solid ${C.brass};
          padding: 12px 16px;
          z-index: 40;
          transform: translateY(100%);
          transition: transform 250ms ease-out;
          box-shadow: 0 -8px 24px rgba(0,0,0,0.3);
        }
        .marketing-sticky.visible { transform: translateY(0); }
      `}</style>

      {/* Contenu principal */}
      <main style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", padding: "24px 20px 48px" }}>
        <div style={{ maxWidth: 1140, width: "100%", display: "grid" }} className="marketing-grid">
          {/* Colonne gauche : texte */}
          <div>
            <div
              data-testid="eyebrow"
              className="marketing-eyebrow"
              style={{ color: C.brassLight, textTransform: "uppercase", letterSpacing: "0.25em", fontWeight: 600 }}
            >
              — Vous venez de perdre un proche ?
            </div>
            <h1
              data-testid="hero-title"
              className="marketing-hero-title"
              style={{ fontFamily: fontSerif, fontWeight: 500, lineHeight: 1.12, margin: 0, color: C.white, letterSpacing: "-0.01em" }}
            >
              5 minutes pour savoir par où commencer.
            </h1>
            <p
              data-testid="hero-body"
              className="marketing-hero-body"
              style={{ color: C.offWhite, maxWidth: 560 }}
            >
              <span className="marketing-hero-body-full">
                Papiers à réunir, notaire, héritiers, biens à évaluer, désaccords possibles…
                En <strong style={{ color: C.brassLight, fontWeight: 700 }}>4 étapes guidées</strong>, obtenez un{" "}
                <strong style={{ color: C.brassLight, fontWeight: 700 }}>diagnostic gratuit</strong> qui identifie
                exactement les professionnels à consulter et les documents à rassembler.
              </span>
              <span className="marketing-hero-body-short">
                En <strong style={{ color: C.brassLight, fontWeight: 700 }}>4 étapes guidées</strong>, obtenez un{" "}
                <strong style={{ color: C.brassLight, fontWeight: 700 }}>diagnostic gratuit</strong> qui identifie
                les professionnels à consulter et les documents à réunir.
              </span>
            </p>

            {/* Badges */}
            <div className="marketing-badges" style={{ display: "flex", flexWrap: "wrap" }}>
              {[
                { lbl: "5 minutes", tid: "badge-time" },
                { lbl: "Gratuit, sans engagement", tid: "badge-free" },
                { lbl: "Adapté aux Outre-mer", tid: "badge-om" },
              ].map((b) => (
                <span
                  key={b.tid}
                  data-testid={b.tid}
                  className="marketing-badge"
                  style={{
                    display: "inline-flex", alignItems: "center", gap: 6,
                    background: "rgba(246,242,233,0.06)",
                    border: "1px solid rgba(246,242,233,0.22)",
                    color: C.white, borderRadius: 999, fontWeight: 600, letterSpacing: "0.02em",
                  }}
                >
                  <span style={{ color: C.brassLight, fontWeight: 700 }}>✓</span>
                  {b.lbl}
                </span>
              ))}
            </div>

            {/* CTAs */}
            <div ref={heroCtaRef} style={{ display: "flex", flexWrap: "wrap", gap: 16, alignItems: "center" }}>
              <a
                href={CONNEXION_URL}
                data-testid="marketing-cta-primary"
                className="marketing-cta"
                style={{
                  display: "inline-block",
                  background: `linear-gradient(180deg, ${C.brassLight} 0%, ${C.brass} 100%)`,
                  color: C.tealDark,
                  padding: "17px 36px",
                  borderRadius: 6,
                  fontWeight: 800,
                  fontSize: 15.5,
                  textDecoration: "none",
                  letterSpacing: "0.03em",
                  boxShadow: "0 6px 22px rgba(173,124,51,0.35)",
                  transition: "transform 0.15s ease, box-shadow 0.15s ease",
                }}
              >
                Commencer mon diagnostic gratuit →
              </a>
              <span
                data-testid="marketing-cta-secondary"
                style={{ color: C.offWhite, fontSize: 13, opacity: 0.75, fontWeight: 500, letterSpacing: "0.02em" }}
              >
                Aucun compte requis · 5 minutes
              </span>
            </div>

            {/* Ligne de réassurance */}
            <div
              data-testid="reassurance-line"
              style={{
                marginTop: 14,
                fontSize: 12.5,
                color: "rgba(246,242,233,0.75)",
                display: "inline-flex",
                alignItems: "center",
                gap: 8,
                letterSpacing: "0.01em",
              }}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={C.brassLight} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M12 2 L4 6 L4 12 c0 5 3.5 8.5 8 10 c4.5 -1.5 8 -5 8 -10 L20 6 Z" />
              </svg>
              Vos informations restent confidentielles.
            </div>

            {/* Note prix */}
            <div
              data-testid="price-note"
              className="marketing-price-note"
              style={{ fontStyle: "italic", color: "rgba(246,242,233,0.6)", maxWidth: 560, lineHeight: 1.55 }}
            >
              Feuille de route complète (documents, ordre des rendez-vous, actions par professionnel) au tarif unique de{" "}
              <strong style={{ color: C.brassLight, fontStyle: "normal", fontWeight: 700 }}>89&nbsp;€</strong>.
            </div>
          </div>

          {/* Colonne droite : carte Aperçu diagnostic (atténuée) */}
          <div>
            <div
              data-testid="preview-card"
              className="marketing-example-fade"
              style={{
                background: C.cream,
                color: C.ink,
                borderRadius: 12,
                padding: "24px 26px 18px",
                boxShadow: "0 20px 42px rgba(0,0,0,0.32)",
                border: "1px solid rgba(0,0,0,0.04)",
                opacity: fade ? 1 : 0,
                transition: "opacity 180ms ease",
                position: "relative",
              }}
            >
              <div
                data-testid="preview-label"
                style={{ color: C.brassDark, textTransform: "uppercase", letterSpacing: "0.22em", fontSize: 11, fontWeight: 700, marginBottom: 10 }}
              >
                Aperçu diagnostic
              </div>
              <div
                data-testid="preview-title"
                style={{ fontFamily: fontSerif, color: C.tealDark, fontSize: 23, fontWeight: 500, lineHeight: 1.2, marginBottom: 14 }}
              >
                {current.titre}
              </div>

              <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 14 }} data-testid="preview-tags">
                {current.tags.map((t, i) => (
                  <span
                    key={i}
                    style={{
                      background: t.color === "teal" ? C.tealDark : C.brass,
                      color: t.color === "teal" ? C.brassLight : C.cream,
                      padding: "4px 12px", borderRadius: 999, fontSize: 11, fontWeight: 700, letterSpacing: "0.03em",
                    }}
                  >
                    {t.label}
                  </span>
                ))}
              </div>

              <div>
                {current.pros.map((p, i) => {
                  const Ico = ICONS[p.key] || IconNotaire;
                  return (
                    <div
                      key={i}
                      data-testid={`preview-pro-${i + 1}`}
                      style={{
                        display: "flex", alignItems: "center", gap: 12,
                        padding: "11px 0",
                        borderBottom: i < current.pros.length - 1 ? `1px solid ${C.creamRule}` : "none",
                        fontSize: 14, color: C.tealDark,
                      }}
                    >
                      <span
                        style={{
                          width: 26, height: 26, borderRadius: "50%",
                          background: `linear-gradient(180deg, ${C.brassLight} 0%, ${C.brass} 100%)`,
                          color: C.tealDark,
                          display: "inline-flex", alignItems: "center", justifyContent: "center",
                          fontSize: 12, fontWeight: 700, flexShrink: 0,
                        }}
                      >
                        {i + 1}
                      </span>
                      <span style={{ color: C.brassDark, display: "inline-flex" }}>
                        <Ico size={20} color={C.brassDark} />
                      </span>
                      <span style={{ flex: 1, fontWeight: 600 }}>{p.label}</span>
                    </div>
                  );
                })}
              </div>

              <div
                data-testid="preview-note"
                style={{ marginTop: 12, fontSize: 11.5, fontStyle: "italic", color: "rgba(23,36,42,0.55)" }}
              >
                Exemple simplifié — votre diagnostic personnalisé sera plus détaillé.
              </div>

              <div style={{ marginTop: 16, textAlign: "center" }}>
                <button
                  onClick={nextExample}
                  data-testid="btn-next-example"
                  style={{
                    background: "transparent", border: "none",
                    color: C.brassDark, fontSize: 13, fontWeight: 600,
                    cursor: "pointer", textDecoration: "underline", textUnderlineOffset: 4,
                    padding: 0, letterSpacing: "0.02em", fontFamily: fontSans,
                  }}
                >
                  Voir un autre exemple →
                </button>
                <div style={{ display: "flex", gap: 6, justifyContent: "center", marginTop: 10 }} data-testid="preview-dots">
                  {EXAMPLES.map((_, i) => (
                    <span
                      key={i}
                      data-testid={`preview-dot-${i}`}
                      style={{
                        width: 6, height: 6, borderRadius: "50%",
                        background: i === idx ? C.brass : "rgba(18,59,54,0.2)",
                        transition: "background 180ms ease",
                      }}
                    />
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* Footer discret */}
      <footer
        data-testid="marketing-footer"
        style={{ padding: "16px 20px 22px", textAlign: "center", fontSize: 12, color: "rgba(246,242,233,0.55)", letterSpacing: "0.02em" }}
      >
        AUPRÉA SAS · <em>Anticiper est un acte d'amour</em>
      </footer>

      {/* Sticky CTA bar (visible quand le CTA du hero est hors viewport) */}
      <div
        className={`marketing-sticky ${!heroCtaVisible ? "visible" : ""}`}
        data-testid="marketing-sticky-bar"
        aria-hidden={heroCtaVisible ? "true" : "false"}
      >
        <div style={{ maxWidth: 1140, margin: "0 auto", display: "flex", alignItems: "center", gap: 12, justifyContent: "space-between" }}>
          <div style={{ fontSize: 12.5, color: "rgba(246,242,233,0.85)", flex: 1, minWidth: 0 }}>
            <strong style={{ color: C.brassLight, fontWeight: 700 }}>Diagnostic gratuit</strong> · 5 minutes · aucun engagement
          </div>
          <a
            href={CONNEXION_URL}
            data-testid="marketing-sticky-cta"
            style={{
              display: "inline-block",
              background: `linear-gradient(180deg, ${C.brassLight} 0%, ${C.brass} 100%)`,
              color: C.tealDark,
              padding: "10px 20px",
              borderRadius: 4,
              fontWeight: 800,
              fontSize: 13,
              textDecoration: "none",
              letterSpacing: "0.03em",
              whiteSpace: "nowrap",
            }}
          >
            Commencer →
          </a>
        </div>
      </div>
    </div>
  );
}
